import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input } from 'antd';
import { FiAlertCircle, FiCheck, FiUpload, FiX } from 'react-icons/fi';
import { HiOutlineDocumentText } from 'react-icons/hi';
import toast from 'react-hot-toast';
import BankManualUploadErrorAlert from './BankManualUploadErrorAlert';
import ifaService from '../../services/ifaService';
import { processSupportingDocumentForUpload } from '../../utils/cmrFileProcessing';

const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

function isImageFile(file) {
  if (!file) return false;
  const mime = String(file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  return /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(String(file.name || ''));
}

function UploadedFilePreview({ file }) {
  const previewUrl = useMemo(() => {
    if (!file || !isImageFile(file)) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  return (
    <div className="flex w-full flex-col items-center gap-3">
      {previewUrl ? (
        <div className="flex max-h-52 w-full items-center justify-center overflow-hidden rounded-lg border border-green-200/80 bg-gray-50">
          <img src={previewUrl} alt={`Preview: ${file.name}`} className="max-h-52 w-full object-contain" />
        </div>
      ) : (
        <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-green-200/70 bg-green-100" aria-hidden>
          <HiOutlineDocumentText className="h-10 w-10 text-green-700" />
        </div>
      )}
      <div className="w-full min-w-0 text-center">
        <p className="break-all text-sm font-medium text-gray-700">{file.name}</p>
        <p className="mt-0.5 text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
      </div>
    </div>
  );
}

function FileUploadField({
  label,
  required = false,
  file,
  error,
  processing,
  disabled,
  inputRef,
  onSelect,
  onClear,
}) {
  return (
    <div>
      <p className="mb-1 text-sm font-medium text-gray-700">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </p>
      {error ? (
        <p className="mb-1 flex items-center gap-1 text-xs text-red-500">
          <FiAlertCircle size={12} />
          {error}
        </p>
      ) : null}
      <div
        role="button"
        tabIndex={0}
        onClick={() => !processing && !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !processing && !disabled) {
            inputRef.current?.click();
          }
        }}
        className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-4 transition-all
          ${processing ? 'cursor-wait border-gray-200 bg-gray-50 opacity-80' : ''}
          ${!processing && file ? 'border-green-400 bg-green-50/50' : ''}
          ${!processing && !file ? 'border-gray-200 bg-gray-50 hover:border-green-400 hover:bg-green-50/30' : ''}`}
      >
        {processing ? (
          <>
            <svg className="h-5 w-5 animate-spin text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-sm text-gray-500">Processing file...</p>
          </>
        ) : file ? (
          <UploadedFilePreview file={file} />
        ) : (
          <>
            <FiUpload size={20} className="text-gray-400" />
            <p className="text-sm text-gray-500">Click to select file</p>
            <p className="text-xs text-gray-400">PDF max 2 MB · Images up to 5 MB (auto-compressed to 2 MB)</p>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png"
        disabled={processing || disabled}
        onChange={(e) => {
          const selected = e.target.files?.[0];
          e.target.value = '';
          if (selected) onSelect(selected);
        }}
      />
      {file && !processing ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-2 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
        >
          <FiX size={12} />
          Remove file
        </button>
      ) : null}
    </div>
  );
}

export default function BankManualUploadForm({
  manualUploadData,
  loading = false,
  onSubmit,
  onCancel,
}) {
  const prefill = manualUploadData?.prefill || {};
  const [accountNumber, setAccountNumber] = useState(prefill.accountNumber || '');
  const [ifscCode, setIfscCode] = useState(prefill.ifsc || '');
  const [bankName, setBankName] = useState(prefill.bankName || '');
  const [branchName, setBranchName] = useState(prefill.branchName || '');
  const [ifscLookupLoading, setIfscLookupLoading] = useState(false);
  const [ifscVerified, setIfscVerified] = useState(Boolean(prefill.bankName && prefill.ifsc));
  const [errors, setErrors] = useState({});
  const [chequeFile, setChequeFile] = useState(null);
  const [otherDocFile, setOtherDocFile] = useState(null);
  const [chequeFileProcessing, setChequeFileProcessing] = useState(false);
  const [otherDocFileProcessing, setOtherDocFileProcessing] = useState(false);
  const chequeFileInputRef = useRef(null);
  const otherDocFileInputRef = useRef(null);

  useEffect(() => {
    setAccountNumber(prefill.accountNumber || '');
    setIfscCode(prefill.ifsc || '');
    setBankName(prefill.bankName || '');
    setBranchName(prefill.branchName || '');
    setIfscVerified(Boolean(prefill.bankName && prefill.ifsc));
    setErrors({});
    setChequeFile(null);
    setOtherDocFile(null);
  }, [manualUploadData]);

  const ifscValue = (ifscCode || '').toUpperCase();

  useEffect(() => {
    if (!IFSC_REGEX.test(ifscValue)) {
      setBankName('');
      setBranchName('');
      setIfscVerified(false);
      return;
    }

    let cancelled = false;
    const lookup = async () => {
      setIfscLookupLoading(true);
      try {
        const response = await ifaService.lookupIFSC(ifscValue);
        const details = response?.data || {};
        if (cancelled) return;
        setBankName(details.bank_name || '');
        setBranchName(details.branch_name || '');
        setIfscVerified(Boolean(details.bank_name));
      } catch {
        if (cancelled) return;
        setBankName('');
        setBranchName('');
        setIfscVerified(false);
      } finally {
        if (!cancelled) setIfscLookupLoading(false);
      }
    };

    lookup();
    return () => {
      cancelled = true;
    };
  }, [ifscValue]);

  const applyChequeFile = async (file) => {
    setChequeFileProcessing(true);
    try {
      const result = await processSupportingDocumentForUpload(file);
      if (!result.ok) {
        toast.error(result.error);
        setErrors((prev) => ({ ...prev, cheque: result.error }));
        return;
      }
      setChequeFile(result.file);
      setErrors((prev) => ({ ...prev, cheque: '' }));
      if (result.wasCompressed) toast.success('Image compressed to under 2 MB.');
    } finally {
      setChequeFileProcessing(false);
    }
  };

  const applyOtherDocFile = async (file) => {
    setOtherDocFileProcessing(true);
    try {
      const result = await processSupportingDocumentForUpload(file);
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      setOtherDocFile(result.file);
      if (result.wasCompressed) toast.success('Image compressed to under 2 MB.');
    } finally {
      setOtherDocFileProcessing(false);
    }
  };

  const handleSubmit = () => {
    const nextErrors = {};
    if (!String(accountNumber || '').trim()) nextErrors.accountNumber = 'Account number is required';
    if (!IFSC_REGEX.test(ifscValue)) nextErrors.ifsc = 'Enter a valid IFSC code';
    if (!bankName.trim()) nextErrors.bankName = 'Bank name is required (use IFSC lookup)';
    if (!chequeFile) nextErrors.cheque = 'Cancelled cheque / supporting document is required';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    onSubmit?.({
      accountNumber: String(accountNumber).trim(),
      ifscCode: ifscValue,
      bankName: bankName.trim(),
      branchName: branchName || '',
      chequeFile,
      otherDocFile,
    });
  };

  return (
    <div className="flex flex-col gap-5">
      <BankManualUploadErrorAlert manualUploadData={manualUploadData} />

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Account number</label>
            <Input
              value={accountNumber}
              onChange={(e) => {
                setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 18));
                setErrors((prev) => ({ ...prev, accountNumber: '' }));
              }}
              placeholder="Enter account number"
              disabled={loading}
            />
            {errors.accountNumber ? <p className="mt-1 text-xs text-red-500">{errors.accountNumber}</p> : null}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">IFSC code</label>
            <div className="relative">
              <Input
                value={ifscValue}
                onChange={(e) => {
                  setIfscCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11));
                  setErrors((prev) => ({ ...prev, ifsc: '' }));
                }}
                placeholder="HDFC0001234"
                maxLength={11}
                disabled={loading}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {ifscLookupLoading ? (
                  <svg className="h-4 w-4 animate-spin text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                ) : null}
                {!ifscLookupLoading && ifscVerified ? <FiCheck size={16} className="text-green-500" /> : null}
              </div>
            </div>
            {errors.ifsc ? <p className="mt-1 text-xs text-red-500">{errors.ifsc}</p> : null}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Bank name</label>
            <Input
              value={bankName}
              placeholder={ifscLookupLoading ? 'Fetching from IFSC...' : 'Auto-filled from IFSC lookup'}
              disabled
            />
            {errors.bankName ? <p className="mt-1 text-xs text-red-500">{errors.bankName}</p> : null}
          </div>

          {branchName ? (
            <div className="rounded-lg border border-green-200 bg-green-50 px-3.5 py-2.5 text-sm text-green-700">
              Branch: <span className="font-medium">{branchName}</span>
            </div>
          ) : null}
        </div>
      </div>

      <FileUploadField
        label="Cancelled cheque / supporting document"
        required
        file={chequeFile}
        error={errors.cheque}
        processing={chequeFileProcessing}
        disabled={loading}
        inputRef={chequeFileInputRef}
        onSelect={applyChequeFile}
        onClear={() => setChequeFile(null)}
      />

      <FileUploadField
        label="Other supporting documents (optional)"
        file={otherDocFile}
        processing={otherDocFileProcessing}
        disabled={loading}
        inputRef={otherDocFileInputRef}
        onSelect={applyOtherDocFile}
        onClear={() => setOtherDocFile(null)}
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Button
          type="primary"
          loading={loading}
          disabled={
            loading
            || chequeFileProcessing
            || otherDocFileProcessing
            || !accountNumber.trim()
            || !ifscVerified
            || !chequeFile
          }
          onClick={handleSubmit}
          className="sm:flex-1"
        >
          Submit for Review
        </Button>
        <Button onClick={onCancel} disabled={loading} className="sm:flex-1">
          Back to bank verification
        </Button>
      </div>
    </div>
  );
}
